<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useTapGuard } from '@/shared/composables/use-tap-guard';

type Axis = 'x' | 'y';

interface Props {
  // Arrows at the two ends; each hides when there is nothing that way.
  arrows?: boolean;
  axis: Axis;
  // Tiles snap to the start of the viewport.
  snap?: boolean;
  // The number of items, watched so the edges update when they change.
  itemCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  arrows: false,
  itemCount: 0,
  snap: true,
});
const emit = defineEmits<{
  visibleItemsChange: [uuids: string[]];
}>();

const scrollable = ref<HTMLElement | null>(null);
const moreAtStart = ref(false);
const moreAtEnd = ref(false);
const guard = useTapGuard();
let resizeObserver: ResizeObserver | null = null;
let frame = 0;
let settleTimer = 0;
let lastVisible = '';

const horizontal = computed(() => props.axis === 'x');

function scrollPosition(element: HTMLElement): {
  offset: number;
  size: number;
  total: number;
} {
  return horizontal.value
    ? {
        offset: element.scrollLeft,
        size: element.clientWidth,
        total: element.scrollWidth,
      }
    : {
        offset: element.scrollTop,
        size: element.clientHeight,
        total: element.scrollHeight,
      };
}

function updateEdges(): void {
  const element = scrollable.value;
  if (!element) {
    moreAtStart.value = false;
    moreAtEnd.value = false;
    return;
  }
  const { offset, size, total } = scrollPosition(element);
  moreAtStart.value = offset > 8;
  moreAtEnd.value = offset + size < total - 8;
}

function itemElements(): HTMLElement[] {
  return Array.from(
    scrollable.value?.querySelectorAll<HTMLElement>('[data-uuid]') ?? []
  );
}

function itemElement(uuid: string): HTMLElement | null {
  return (
    itemElements().find((element) => element.dataset.uuid === uuid) ?? null
  );
}

// An item counts as visible when most of it is inside the viewport along
// the scroll axis: a tile that only peeks in is the next one, not this one.
function visibleRatio(item: HTMLElement): number {
  const element = scrollable.value;
  if (!element) {
    return 0;
  }
  const box = element.getBoundingClientRect();
  const rect = item.getBoundingClientRect();
  const [start, end, itemStart, itemEnd] = horizontal.value
    ? [box.left, box.right, rect.left, rect.right]
    : [box.top, box.bottom, rect.top, rect.bottom];
  const length = itemEnd - itemStart;
  if (length <= 0) {
    return 0;
  }
  const overlap = Math.min(end, itemEnd) - Math.max(start, itemStart);
  return Math.max(0, overlap) / length;
}

function visibleUuids(): string[] {
  return itemElements()
    .filter((item) => visibleRatio(item) >= 0.6)
    .map((item) => item.dataset.uuid)
    .filter((uuid): uuid is string => Boolean(uuid));
}

function emitVisibleItems(): void {
  const uuids = visibleUuids();
  const key = uuids.join('|');
  if (key !== lastVisible) {
    lastVisible = key;
    emit('visibleItemsChange', uuids);
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onScroll(): void {
  guard.onScroll();
  if (frame) {
    cancelAnimationFrame(frame);
  }
  frame = requestAnimationFrame(() => {
    frame = 0;
    updateEdges();
  });
  window.clearTimeout(settleTimer);
  settleTimer = window.setTimeout(emitVisibleItems, 120);
}

function scrollTo(offset: number, behavior: ScrollBehavior = 'smooth'): void {
  const element = scrollable.value;
  if (!element) {
    return;
  }
  const { size, total } = scrollPosition(element);
  const target = Math.max(0, Math.min(total - size, offset));
  if (typeof element.scrollTo !== 'function') {
    if (horizontal.value) {
      element.scrollLeft = target;
    } else {
      element.scrollTop = target;
    }
    return;
  }
  element.scrollTo(
    horizontal.value ? { left: target, behavior } : { top: target, behavior }
  );
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function scrollByViewport(direction: 1 | -1): void {
  const element = scrollable.value;
  if (!element) {
    return;
  }
  const { offset, size } = scrollPosition(element);
  scrollTo(offset + direction * size * 0.9);
}

function canScroll(): boolean {
  const element = scrollable.value;
  if (!element) {
    return false;
  }
  const { size, total } = scrollPosition(element);
  return total > size + 2;
}

// null: the item is not in this scroller.
function isItemVisible(uuid: string): boolean | null {
  const item = itemElement(uuid);
  return item ? visibleRatio(item) >= 0.6 : null;
}

function scrollToItem(
  uuid: string,
  behavior: ScrollBehavior = 'smooth'
): boolean {
  const element = scrollable.value;
  const item = itemElement(uuid);
  if (!(element && item)) {
    return false;
  }
  const box = element.getBoundingClientRect();
  const rect = item.getBoundingClientRect();
  const { offset } = scrollPosition(element);
  const delta = horizontal.value ? rect.left - box.left : rect.top - box.top;
  scrollTo(offset + delta, behavior);
  return true;
}

watch(
  () => props.itemCount,
  () => {
    requestAnimationFrame(() => {
      updateEdges();
      emitVisibleItems();
    });
  }
);

onMounted(() => {
  updateEdges();
  emitVisibleItems();
  if (typeof ResizeObserver !== 'undefined' && scrollable.value) {
    resizeObserver = new ResizeObserver(updateEdges);
    resizeObserver.observe(scrollable.value);
    // The tiles grow when their images report a ratio; the viewport does
    // not, so the content is watched too or the edges go stale.
    const content = scrollable.value.firstElementChild;
    if (content) {
      resizeObserver.observe(content);
    }
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  if (frame) {
    cancelAnimationFrame(frame);
  }
  window.clearTimeout(settleTimer);
});

defineExpose({ canScroll, isItemVisible, scrollToItem });
</script>

<template>
  <div
    class="ClassicMixedPortraitGallery__scroller"
    :class="[
      `ClassicMixedPortraitGallery__scroller--${axis}`,
      {
        'ClassicMixedPortraitGallery__scroller--more-start': moreAtStart,
        'ClassicMixedPortraitGallery__scroller--more-end': moreAtEnd,
        'ClassicMixedPortraitGallery__scroller--arrows': arrows,
      },
    ]"
    @pointerdown.capture="guard.onPointerDown"
    @pointercancel.capture="guard.onPointerCancel"
    @click.capture="guard.onClickCapture"
  >
    <div
      ref="scrollable"
      class="ClassicMixedPortraitGallery__scroller-viewport"
      :class="{ 'ClassicMixedPortraitGallery__scroller-viewport--snap': snap }"
      data-testid="classic-mixed-gallery-scroller"
      @scroll.passive="onScroll"
    >
      <slot />
    </div>
    <button
      v-if="arrows"
      v-show="moreAtStart"
      class="ClassicMixedPortraitGallery__control ClassicMixedPortraitGallery__arrow ClassicMixedPortraitGallery__scroller-arrow ClassicMixedPortraitGallery__scroller-arrow--start"
      type="button"
      data-testid="classic-mixed-gallery-prev"
      :aria-label="$t('Portraits.galleryScrollBack')"
      @click="scrollByViewport(-1)"
    />
    <button
      v-if="arrows"
      v-show="moreAtEnd"
      class="ClassicMixedPortraitGallery__control ClassicMixedPortraitGallery__arrow ClassicMixedPortraitGallery__scroller-arrow ClassicMixedPortraitGallery__scroller-arrow--end"
      type="button"
      data-testid="classic-mixed-gallery-next"
      :aria-label="$t('Portraits.galleryScrollForward')"
      @click="scrollByViewport(1)"
    />
  </div>
</template>

<style scoped lang="scss">
@use './classic-gallery-control';

/* A native scroll viewport: momentum, snap and overscroll come from the
   browser. It is a size container so the tiles inside can take their height
   from it (cqh) or their width (cqw); its edges fade where there is more. */
.ClassicMixedPortraitGallery__scroller {
  --cmg-scroller-fade: 3rem;

  position: relative;
  min-width: 0;
  min-height: 0;
}

.ClassicMixedPortraitGallery__scroller-viewport {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  overscroll-behavior: contain;
  scrollbar-width: none;
  container-type: size;
  -webkit-overflow-scrolling: touch;
}

.ClassicMixedPortraitGallery__scroller-viewport::-webkit-scrollbar {
  display: none;
}

.ClassicMixedPortraitGallery__scroller--x .ClassicMixedPortraitGallery__scroller-viewport {
  overflow-x: auto;
  touch-action: pan-x;
}

.ClassicMixedPortraitGallery__scroller--y .ClassicMixedPortraitGallery__scroller-viewport {
  overflow-y: auto;
  touch-action: pan-y;
}

.ClassicMixedPortraitGallery__scroller--x .ClassicMixedPortraitGallery__scroller-viewport--snap {
  scroll-snap-type: x mandatory;
}

.ClassicMixedPortraitGallery__scroller--y .ClassicMixedPortraitGallery__scroller-viewport--snap {
  scroll-snap-type: y proximity;
}

/* The fade is on the viewport so the tiles under it dim, not the arrows. */
.ClassicMixedPortraitGallery__scroller--x.ClassicMixedPortraitGallery__scroller--more-end
  .ClassicMixedPortraitGallery__scroller-viewport {
  mask-image: linear-gradient(
    to right,
    #000 calc(100% - var(--cmg-scroller-fade)),
    transparent
  );
}

.ClassicMixedPortraitGallery__scroller--x.ClassicMixedPortraitGallery__scroller--more-start
  .ClassicMixedPortraitGallery__scroller-viewport {
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 var(--cmg-scroller-fade)
  );
}

.ClassicMixedPortraitGallery__scroller--x.ClassicMixedPortraitGallery__scroller--more-start.ClassicMixedPortraitGallery__scroller--more-end
  .ClassicMixedPortraitGallery__scroller-viewport {
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 var(--cmg-scroller-fade),
    #000 calc(100% - var(--cmg-scroller-fade)),
    transparent
  );
}

.ClassicMixedPortraitGallery__scroller--y.ClassicMixedPortraitGallery__scroller--more-end
  .ClassicMixedPortraitGallery__scroller-viewport {
  mask-image: linear-gradient(
    to bottom,
    #000 calc(100% - var(--cmg-scroller-fade)),
    transparent
  );
}

.ClassicMixedPortraitGallery__scroller--y.ClassicMixedPortraitGallery__scroller--more-start
  .ClassicMixedPortraitGallery__scroller-viewport {
  mask-image: linear-gradient(
    to bottom,
    transparent,
    #000 var(--cmg-scroller-fade)
  );
}

.ClassicMixedPortraitGallery__scroller--y.ClassicMixedPortraitGallery__scroller--more-start.ClassicMixedPortraitGallery__scroller--more-end
  .ClassicMixedPortraitGallery__scroller-viewport {
  mask-image: linear-gradient(
    to bottom,
    transparent,
    #000 var(--cmg-scroller-fade),
    #000 calc(100% - var(--cmg-scroller-fade)),
    transparent
  );
}

.ClassicMixedPortraitGallery__scroller-arrow {
  position: absolute;
  z-index: 4;
}

.ClassicMixedPortraitGallery__scroller--x .ClassicMixedPortraitGallery__scroller-arrow {
  top: 50%;
  transform: translateY(-50%);
}

.ClassicMixedPortraitGallery__scroller--x .ClassicMixedPortraitGallery__scroller-arrow--start {
  left: 0.6rem;
}

.ClassicMixedPortraitGallery__scroller--x .ClassicMixedPortraitGallery__scroller-arrow--start::after {
  transform: rotate(180deg);
}

.ClassicMixedPortraitGallery__scroller--x .ClassicMixedPortraitGallery__scroller-arrow--end {
  right: 0.6rem;
}

.ClassicMixedPortraitGallery__scroller--y .ClassicMixedPortraitGallery__scroller-arrow {
  left: 50%;
  transform: translateX(-50%);
}

.ClassicMixedPortraitGallery__scroller--y .ClassicMixedPortraitGallery__scroller-arrow--start {
  top: 0.6rem;
}

.ClassicMixedPortraitGallery__scroller--y .ClassicMixedPortraitGallery__scroller-arrow--start::after {
  transform: rotate(-90deg);
}

.ClassicMixedPortraitGallery__scroller--y .ClassicMixedPortraitGallery__scroller-arrow--end {
  bottom: 0.6rem;
}

.ClassicMixedPortraitGallery__scroller--y .ClassicMixedPortraitGallery__scroller-arrow--end::after {
  transform: rotate(90deg);
}
</style>
